import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import AddSearchResultsToGroup from '~/components/SearchV2/AddSearchResultsToGroup/AddSearchResultsToGroup';
import AddSearchResultsToGroupV2 from '~/components/SearchV2/AddSearchResultsToGroupV2/AddSearchResultsToGroupV2';
import CopyMoveSearchResults from '~/components/SearchV2/CopyMoveSearchResults/CopyMoveSearchResults';
import DeleteSearchResults from '~/components/SearchV2/DeleteSearchResults/DeleteSearchResults';
import EditInAnalyzer from '~/components/SearchV2/EditInAnalyzer/EditInAnalyzer';
import EditSearchResults from '~/components/SearchV2/EditSearchResults/EditSearchResults';
import ExportSearchResults from '~/components/SearchV2/ExportSearchResults/ExportSearchResults';
import SearchBarControls from '~/components/SearchV2/SearchBarControls/SearchBarControls';
import SearchFilters from '~/components/SearchV2/SearchFilters/SearchFilters';
import SearchResult from '~/components/SearchV2/SearchResult/SearchResult';
import { getHandlersKeyFromSearchResult } from '~/components/SearchV2/SearchResult/SearchResult.utils';
import {
  flattenFolderTree,
  toFilter,
} from '~/components/SearchV2/SearchV2.utils';
import { useDocumentMultipleLocationsModal } from '~/components/Shared/EcModal/useDocumentMultipleLocationsModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useTableSelection } from '~/eds/src/hooks/useTableSelection';
import { TableState } from '~/eds/src/types/Table';
import { BulkActionType } from '~/enums/BulkActionType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { TableContextType } from '~/enums/TableContextType';
import {
  DEFAULT_SEARCH_ID,
  getDefaultChatContext,
} from '~/features/ask-anything/ask-anything-button/utils';
import { useDocumentsBulkActionsPanel } from '~/features/search/useDocumentsBulkActionsPanel';
import { convertQueryV2ToQueryV3 } from '~/features/search/utils/convertQueryV2ToQueryV3';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { usePinnedFilters as usePinnedFiltersDeprecated } from '~/hooks/usePinnedFilters_DEPRECATED';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { toSearchField } from '~/redux/api/methods/search';
import { PersistRedux } from '~/redux/persist';
import * as selectors from '~/redux/selectors';
import { selectSearchResultsCache } from '~/redux/selectors';
import { slices } from '~/redux/slices';
import { initContext } from '~/redux/slices/documentsNavigation';
import searchSlice, { pathsToPersist } from '~/redux/slices/search';
import { ColumnSortOrder } from '~/types/Columns';
import { FolderTree } from '~/types/FolderTree';
import {
  MODAL_DELETE,
  MODAL_DOCUMENT_GROUP,
  MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
  MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
  MODAL_EXCEL_EXPORT,
  MODAL_FOLDER_TREE,
  MODAL_MULTI_EDIT,
} from '~/types/modal.types';
import { ERROR } from '~/types/toast.types';
import { defaultErrorMessage } from '~/utils/codedError';
import { parseNavigationResponse } from '~/utils/navigation';

function Search() {
  const dispatch = useDispatch();
  const intl = useIntl();

  const handleOnNavigate = async () => {
    const initOptions = {
      api: navigationAPI,
      getPosition,
      name: 'search results',
      parseResponse: parseNavigationResponse,
      fromLocation: {
        pathname: '/search',
        hash: '',
        search: '',
      },
    };
    // @ts-ignore -- skip typing legacy code from redux upgrade
    dispatch(initContext(initOptions as any));
  };

  const [
    setDocumentSelected,
    renderMultipleLocationModal,
    setOpenDocumentInNewTab,
  ] = useDocumentMultipleLocationsModal(
    getHandlersKeyFromSearchResult,
    false,
    handleOnNavigate,
  );

  const query = useSelector(selectors.selectQuery);
  const pilotSearchQuery = useSelector(selectors.selectPilotSearchQuery);
  const unsupportedSavedSearchQuery = useSelector(
    selectors.selectUnsupportedSavedSearchQuery,
  );
  const queryFields = useSelector(selectors.selectQueryFields);
  const filters = useSelector(selectors.selectActiveSearchFilters);
  const filtersRef = useRef(filters);
  const booleanQuery = useSelector(selectors.selectBooleanQuery);
  const pageSize = useSelector(selectors.selectSearchPageSize);
  const page = useSelector(selectors.selectSearchPage);
  const sortBy = useSelector(selectors.selectSearchSortBy);
  const resultsCache = useSelector(selectSearchResultsCache);

  const tableSelection = useTableSelection();

  const shouldUseNewGroupUI = useHasFeatureFlag(
    FeatureFlagType.DocumentGroupRevamp,
  );
  const isBulkActionsOnSidepanelEnabled = useFlag(
    FlagType.DocumentsBulkActionsOnSidepanel,
  );

  const bulkActionsContext = useSelector(selectors.selectSearchResultModal);

  const { panel, limitExceededModal, setActivePanel } =
    useDocumentsBulkActionsPanel({
      selectedDocumentIds: (bulkActionsContext?.context as TableState)
        ?.selectedRowIds,
      isAllSelected: (bulkActionsContext?.context as TableState)
        ?.isAllRowsSelected,
      totalCount: (bulkActionsContext?.context as TableState)?.totalCount,
      query: pilotSearchQuery,
      searchAPI: 'v2',
      onActionCompleted: refreshSearchResult,
      resultsCache: resultsCache,
    });

  const [pilotSearchDocuments, pilotSearchDocumentsResult] =
    api.endpoints.pilotSearchDocuments.useLazyQuery();

  useEffect(() => {
    if (pilotSearchDocumentsResult.isError) {
      showToast(
        ERROR,
        get(
          pilotSearchDocumentsResult,
          'error.response.data.detail',
          defaultErrorMessage,
        ),
      );
    }
  }, [pilotSearchDocumentsResult.isError]);

  const { data: sections, isFetching: isFetchingSections } =
    api.endpoints.getFilterSections.useQuery(undefined);
  const {
    getPinnedFilters,
    result: {
      data: userPreferencesDefaultFilters,
      isFetching: isFetchingPinnedFilters,
      isSuccess: isSuccessPinnedFilters,
    },
  } = usePinnedFiltersDeprecated(sections?.fields);

  const isLoadingPinnedFilters =
    !isSuccessPinnedFilters || isFetchingPinnedFilters;
  const [getPositionByHandler] =
    api.endpoints.getSearchPositionByHandler.useLazyQuery();
  const [getHandlersByPosition] =
    api.endpoints.getSearchHandlersByPosition.useLazyQuery();

  const { data: clauseSuggestions } =
    api.endpoints.getClauseSuggestions.useQuery(undefined);

  const { data: folderTreeData } =
    api.endpoints.getFolderTree.useQuery(undefined);

  const { tableSettings } = useTableSettings(TableContextType.Search);
  useEffect(() => {
    if (tableSettings) {
      dispatch(searchSlice.actions.setColumnOrder(tableSettings.columnOrder));
      dispatch(
        searchSlice.actions.patchColumnWidths(tableSettings.columnWidths),
      );
      dispatch(searchSlice.actions.setPageSize(tableSettings.pageSize));
      dispatch(searchSlice.actions.setSortBy(tableSettings.sortBy));
    }
  }, [tableSettings]);

  const fields = sections?.fields;
  const fieldGroups = sections?.fieldGroups;

  const hasNewPins = isLoadingPinnedFilters || userPreferencesDefaultFilters;
  const defaultFilters = useMemo(() => {
    return hasNewPins
      ? userPreferencesDefaultFilters
      : sections?.defaultFilters
        ? [...sections?.defaultFilters, ...['folder', 'clause'].map(toFilter)]
        : sections?.defaultFilters;
  }, [hasNewPins, sections?.defaultFilters]);

  const folders = folderTreeData?.children;
  const folderTree = useMemo(
    () => (!folders ? [] : flattenFolderTree(folders as FolderTree[])),
    [folders],
  );

  const filterAreDirty = useSelector(selectors.selectSearchFiltersAreDirty);

  useEffect(() => {
    getPinnedFilters('SEARCH');
  }, []);

  useEffect(() => {
    if (!filterAreDirty && defaultFilters?.length) {
      dispatch(searchSlice.actions.setFilters(defaultFilters));
    }
    if (defaultFilters?.length) {
      dispatch(searchSlice.actions.setDefaultFilters(defaultFilters));
    }
  }, [defaultFilters]);

  const searchDocumentsParams = {
    query: pilotSearchQuery,
    fields: queryFields.map((item) => toSearchField(item)),
    page,
    pageSize,
    sortBy,
  };

  useEffect(() => {
    if (query || unsupportedSavedSearchQuery) {
      pilotSearchDocuments(searchDocumentsParams);
    }
  }, [query, queryFields, page, pageSize, sortBy, unsupportedSavedSearchQuery]);

  useEffect(() => {
    if (
      fields &&
      (filters.length || filtersRef.current.length) &&
      filterAreDirty
    ) {
      dispatch(searchSlice.actions.clearUnsupportedQuery());
      dispatch(searchSlice.actions.setQuery({ booleanQuery, filters, fields }));
    }
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (folderTree.length) {
      dispatch(
        searchSlice.actions.setFolderTree({
          fieldId: 'folder',
          values: folderTree,
        }),
      );
    }
  }, [folderTree]);

  useEffect(() => {
    if (clauseSuggestions) {
      dispatch(
        searchSlice.actions.setClauseSuggestions({
          fieldId: 'clause',
          values: clauseSuggestions.suggestions,
        }),
      );
    }
  }, [clauseSuggestions]);

  const handlePaginate = useCallback(({ page = 1 }) => {
    dispatch(searchSlice.actions.setPage(page));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    dispatch(searchSlice.actions.setPageSize(pageSize));
  }, []);

  const handleSortBy = useCallback((sortBy: ColumnSortOrder) => {
    dispatch(searchSlice.actions.setSortBy(sortBy));
  }, []);

  const { data, error, isFetching } =
    api.endpoints.pilotSearchDocuments.useQueryState(searchDocumentsParams);

  useEffect(() => {
    if (data?.results) {
      dispatch(searchSlice.actions.patchSearchResultsCache(data.results));
      if (data?.results.length > 0) {
        dispatch(
          slices.chatbot.actions.setContext([
            getDefaultChatContext(intl),
            {
              label: 'All Results',
              query: convertQueryV2ToQueryV3(
                searchDocumentsParams?.query ?? [],
              ),
              title: `All Results (${data.count})`,
              icon: 'search',
              primary: true,
              searchEntity: 'document',
              selectedIds: [],
              id: DEFAULT_SEARCH_ID,
            },
          ]),
        );
      }
    }
  }, [data]);

  const handleAddToGroup = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: MODAL_DOCUMENT_GROUP,
        context: tableState,
      }),
    );
  }, []);
  const handleCopyOrMove = useCallback(
    (bulkAction: BulkActionType, tableState: TableState) => {
      setActivePanel(bulkAction);

      dispatch(
        searchSlice.actions.setModal({
          modalType: MODAL_FOLDER_TREE,
          context: { ...tableState, bulkAction },
        }),
      );
    },
    [],
  );
  const handleCopy = useCallback(
    (tableState: TableState) => {
      handleCopyOrMove('copy', tableState);
    },
    [handleCopyOrMove],
  );
  const handleMove = useCallback(
    (tableState: TableState) => {
      handleCopyOrMove('move', tableState);
    },
    [handleCopyOrMove],
  );
  const handleEdit = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: MODAL_MULTI_EDIT,
        context: {
          ...tableState,
        },
      }),
    );
    setActivePanel('edit');
  }, []);

  const handleEditClauses = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: '',
        context: {
          ...tableState,
        },
      }),
    );
    setActivePanel('edit-clauses');
  }, []);

  const handleDelete = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: MODAL_DELETE,
        context: { ...tableState },
      }),
    );
    setActivePanel('delete');
  }, []);

  const resetSearchResultSelection = () => {
    tableSelection.clear();
  };

  useEffect(() => {
    resetSearchResultSelection();
  }, [query]);
  useEffect(() => {
    dispatch(
      slices.chatbot.actions.setContext([
        { ...getDefaultChatContext(intl), primary: true },
      ]),
    );
  }, []);

  function refreshSearchResult() {
    resetSearchResultSelection();
    pilotSearchDocuments(searchDocumentsParams);
  }

  const navigationAPI = (position: number) => {
    return getHandlersByPosition({
      order_by_field: sortBy.id,
      order: sortBy.desc ? 'desc' : 'asc',
      query: pilotSearchQuery!,
      position,
    }).unwrap();
  };

  const getPosition = (handler_id: number) => {
    return getPositionByHandler({
      order_by_field: sortBy.id,
      order: sortBy.desc ? 'desc' : 'asc',
      handler_id,
      query: pilotSearchQuery!,
    }).unwrap();
  };

  const showExportModal = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: MODAL_EXCEL_EXPORT,
        context: { ...tableState },
      }),
    );
  }, []);

  const showSaveView = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE,
        context: { ...tableState },
      }),
    );
  }, []);

  const showLoadView = useCallback((tableState: TableState) => {
    dispatch(
      searchSlice.actions.setModal({
        modalType: MODAL_DOCUMENTS_COLUMN_FIELDS_VIEW_SWITCH,
        context: { ...tableState },
      }),
    );
  }, []);

  const [exportExcel, exportResult] = api.endpoints.exportExcel.useLazyQuery();
  const { isFetching: isExporting } = exportResult;

  return (
    <>
      {pathsToPersist.map((path, index) => (
        <PersistRedux key={index} path={path!} />
      ))}
      <SearchBarControls />
      {!isFetchingSections &&
        fields &&
        fieldGroups &&
        !unsupportedSavedSearchQuery && (
          <SearchFilters
            fields={fields}
            groups={fieldGroups}
            showInlineManageFilters={false}
          />
        )}
      {unsupportedSavedSearchQuery && (
        <Layout spacing={4}>
          <EditInAnalyzer query={unsupportedSavedSearchQuery} />
        </Layout>
      )}
      {!isFetching && !query && !unsupportedSavedSearchQuery ? (
        <EmptyPage preset="no-search" />
      ) : (
        <SearchResult
          data={data}
          error={error}
          isLoading={isFetching}
          onPaginate={handlePaginate}
          //Missing type in useDocumentMultipleLocationsModal
          onViewResult={(document: any, openInNewTab: boolean) => {
            setDocumentSelected(document);
            // @ts-ignore action param type
            setOpenDocumentInNewTab(openInNewTab);
          }}
          tableSelection={tableSelection}
          page={page}
          pageSize={pageSize}
          onAddToGroup={handleAddToGroup}
          onCopy={handleCopy}
          onMove={handleMove}
          onEdit={handleEdit}
          onEditClauses={handleEditClauses}
          onDelete={handleDelete}
          onExport={showExportModal}
          onSaveView={showSaveView}
          onLoadView={showLoadView}
          onNavigate={handleOnNavigate}
          isExporting={isExporting}
          resultsCache={resultsCache}
          sortBy={sortBy}
          onSort={handleSortBy}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
      {shouldUseNewGroupUI ? (
        <AddSearchResultsToGroupV2 />
      ) : (
        <AddSearchResultsToGroup />
      )}
      {!isBulkActionsOnSidepanelEnabled ? (
        <>
          <CopyMoveSearchResults
            onActionComplete={refreshSearchResult}
            resultsCache={resultsCache}
          />
          <EditSearchResults
            onActionComplete={refreshSearchResult}
            resultsCache={resultsCache}
          />
          <DeleteSearchResults
            onActionComplete={refreshSearchResult}
            resultsCache={resultsCache}
          />
        </>
      ) : (
        <>
          {limitExceededModal}
          {panel}
        </>
      )}
      <ExportSearchResults
        onActionComplete={refreshSearchResult}
        searchResult={data}
        onExport={exportExcel}
        exportResult={exportResult}
      />
      {renderMultipleLocationModal(null)}
    </>
  );
}

export default Search;
